import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import type { Insight } from "@/types/insight";
import InsightCardHeader from "./InsightCardHeader";
import InsightCardFooter from "./InsightCardFooter";
import { getInsightPath } from "@/utils/insightSlugs";
import { getOptimizedImageUrl, getThumbnailUrl } from "@/utils/imageUtils";
import { cn } from "@/lib/utils";
import { ImageIcon } from "lucide-react";

interface InsightCardProps {
  insight: Insight;
  language?: string;
  priority?: boolean;
  /**
   * When true, renders an empty 3:2 image placeholder if the insight has no
   * thumbnail (used in the editor preview so the author sees where the image
   * will sit). Public cards keep their current behavior and omit the image.
   */
  showImagePlaceholder?: boolean;
}

const LIKED_KEY = "duru_liked_insights";

const getLikedIds = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(LIKED_KEY) || "[]");
  } catch {
    return [];
  }
};

const InsightCard = ({ insight, language = 'EN', priority = false, showImagePlaceholder = false }: InsightCardProps) => {
  const queryClient = useQueryClient();
  const [isLiked, setIsLiked] = useState(() => getLikedIds().includes(insight.id));
  const [likesCount, setLikesCount] = useState(insight.likes_count || 0);

  const shareUrl = window.location.origin + getInsightPath(insight, language);
  const shareTitle = language === 'KO' && insight.title_ko ? insight.title_ko : insight.title_en;

  const handleLike = async () => {
    try {
      const nowLiked = !isLiked;
      const delta = nowLiked ? 1 : -1;

      const { error } = await supabase
        .rpc('increment_insight_likes', { insight_id: insight.id, delta });

      if (error) throw error;

      const newLikesCount = likesCount + delta;

      // Persist liked state in localStorage so it survives page refresh
      const liked = getLikedIds();
      const updated = nowLiked
        ? [...liked, insight.id]
        : liked.filter((id) => id !== insight.id);
      localStorage.setItem(LIKED_KEY, JSON.stringify(updated));

      setIsLiked(nowLiked);
      setLikesCount(newLikesCount);
      // Invalidate cache so the refreshed count is used on next page load
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    } catch (error) {
      console.error('Error updating likes:', error);
    }
  };

  return (
    <Card className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md shadow-gray-200/60 transition-all duration-300 hover:-translate-y-1 hover:border-primary/25 hover:shadow-2xl hover:shadow-gray-300/50">
      {insight.thumbnail_url && (
        <div className="relative aspect-[3/2] overflow-hidden bg-gray-100">
          <img
            src={getThumbnailUrl(insight.thumbnail_url)}
            alt={language === 'KO' && insight.title_ko ? insight.title_ko : insight.title_en}
            className={cn(
              "h-full w-full object-cover transition-all duration-500 group-hover:scale-105",
              "opacity-0"
            )}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            onLoad={(e) => e.currentTarget.classList.replace("opacity-0", "opacity-100")}
            onError={(e) => {
              const img = e.currentTarget;
              const stage = img.dataset.fallback;
              if (!stage) {
                img.dataset.fallback = "render";
                img.src = getOptimizedImageUrl(insight.thumbnail_url, { width: 400, quality: 80 });
              } else if (stage === "render") {
                img.dataset.fallback = "original";
                img.src = insight.thumbnail_url!;
              }
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-black/0 to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-80" />
        </div>
      )}

      {!insight.thumbnail_url && showImagePlaceholder && (
        <div className="relative flex aspect-[3/2] flex-col items-center justify-center gap-2 overflow-hidden bg-gray-100 text-gray-400">
          <ImageIcon className="h-10 w-10" />
          <span className="text-xs font-medium">Thumbnail preview</span>
        </div>
      )}

      <div className="flex flex-grow flex-col p-5">
        <div className="flex-grow">
          <InsightCardHeader insight={insight} language={language} />
        </div>

        <InsightCardFooter
          readingTime={insight.reading_time || 5}
          likesCount={likesCount}
          viewsCount={insight.views_count || 0}
          isLiked={isLiked}
          onLike={handleLike}
          url={shareUrl}
          title={shareTitle}
          thumbnailUrl={insight.thumbnail_url ?? undefined}
        />
      </div>
    </Card>
  );
};

export default InsightCard;
